import { Check, Copy } from "lucide-react";
import { useCallback, useState } from "react";

import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";

const AGENT_HANDOFF_PROMPT = `List my product on AgentMRR.

Follow https://agentmrr.com/skill.md and submit with one POST.`;

function useCopyToClipboard(text: string) {
	const [copied, setCopied] = useState(false);

	const copy = useCallback(async () => {
		await navigator.clipboard.writeText(text);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	}, [text]);

	return { copied, copy };
}

function AgentPromptDialog({
	open,
	onOpenChange,
	label,
	prompt,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	label: string;
	prompt: string;
}) {
	const { copied, copy } = useCopyToClipboard(prompt);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-md gap-0 rounded-none border-border p-0">
				<div className="flex items-center justify-between border-b border-border px-5 py-3">
					<span className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
						&gt; {label}
					</span>
				</div>
				<DialogHeader className="space-y-1 p-5 text-left">
					<DialogTitle className="sr-only">{label}</DialogTitle>
					<DialogDescription className="text-sm leading-[1.55]">
						Copy this prompt and paste it into your agent.
					</DialogDescription>
				</DialogHeader>
				<div className="px-5 pb-5">
					<button
						type="button"
						onClick={copy}
						className={`group flex w-full flex-col border p-4 text-left transition-[color,background-color,border-color,transform] active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
							copied
								? "border-brand/40 bg-brand-soft"
								: "border-border bg-background hover:border-brand/30 hover:bg-brand-soft"
						}`}
					>
						<pre className="w-full whitespace-pre-wrap font-mono text-sm leading-[1.6] text-foreground">
							{prompt}
						</pre>
						<div className="mt-3 flex items-center gap-1.5 font-mono text-sm uppercase tracking-[0.14em]">
							{copied ? (
								<>
									<Check className="size-3 text-brand" />
									<span className="text-brand">Copied</span>
								</>
							) : (
								<>
									<Copy className="size-3 text-muted-foreground group-hover:text-brand" />
									<span className="text-muted-foreground group-hover:text-brand">
										Click to copy
									</span>
								</>
							)}
						</div>
					</button>
				</div>
			</DialogContent>
		</Dialog>
	);
}

export function AgentHandoffDialog({
	open,
	onOpenChange,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}) {
	return (
		<AgentPromptDialog
			open={open}
			onOpenChange={onOpenChange}
			label="Agent handoff"
			prompt={AGENT_HANDOFF_PROMPT}
		/>
	);
}
